import React, { useCallback, useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material//Clear';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { CustomUser } from 'api';
import { COMPANY, COMPANY_USER_VALUE } from 'common/constants/user';
import { saveUserPermissions, useSaveUserPermissions } from 'services/hooks/useManageUser/useManageUser';
import { getUserPermissions } from 'services/hooks/useUserSelected';
import { Logger } from 'utilities';
import CompanyUsersTable from './CompanyUsersTable';
import { ManageCompanyUsersLedgerProps } from './types';
import UserDetailsDialog from './UserDetailsDialog';

const ManageCompanyUsersLedger = ({
  companyList,
  firmUsers,
  firmId,
  fundList,
  handleManageCompanyUsersDialogClose,
}: ManageCompanyUsersLedgerProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { updatePermissionsForCompOrFundRole } = useSaveUserPermissions();
  const [emails, setEmails] = useState<{ [key: number]: string[] }>({});
  const [openUserDetailsDialog, setOpenUserDetailsDialog] = useState(false);
  const [newPendingUsers, setNewPendingUsers] = useState<{ email: string; companyId: number }[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const filteredCompanies = useMemo(
    () => companyList.filter(company => company.company_name.toLowerCase().includes(searchTerm.toLowerCase())),
    [companyList, searchTerm]
  );

  const getFormattedUsers = (emailList: string[], usersByFirm: CustomUser[]) =>
    emailList.map(email => {
      const user = usersByFirm.find(userFirm => userFirm.email === email);
      return {
        user_id: user?.id ?? null,
        user_email: email,
      };
    });

  const formatEmailsForSaving = useCallback(
    () =>
      Object.entries(emails).map(([companyId, emailList]) => ({
        company_id: Number(companyId),
        users: getFormattedUsers(emailList, firmUsers),
      })),
    [emails, firmUsers]
  );

  const handleSavePermissions = useCallback(() => {
    const formattedEmails = formatEmailsForSaving();
    const tmpNewPendingUsers = formattedEmails.flatMap(({ company_id, users }) =>
      users.filter(user => user.user_id === null).map(user => ({ email: user.user_email, companyId: company_id }))
    );
    setNewPendingUsers(tmpNewPendingUsers);
    setOpenUserDetailsDialog(true);

    formattedEmails.forEach(({ company_id, users }) => {
      users.forEach(async user => {
        if (user.user_id !== null) {
          let prevPermissions = await getUserPermissions(user.user_id, firmId);
          if (!prevPermissions || !Array.isArray(prevPermissions.permissions)) {
            prevPermissions = { permissions: [] };
          }
          const userPermissionsToSave = updatePermissionsForCompOrFundRole(
            COMPANY,
            company_id,
            prevPermissions,
            COMPANY_USER_VALUE,
            firmId,
            user.user_id
          );
          try {
            await saveUserPermissions({
              userId: user.user_id,
              roleType: COMPANY_USER_VALUE,
              firmId,
              userPermissionsToSave,
            });
          } catch (error) {
            enqueueSnackbar(`Failed to save permissions for user ${user.user_email}`, { variant: 'error' });
            Logger.error(`Failed to save permissions for user ${user.user_email}`, error);
          }
        }
      });
    });

    if (isEmpty(tmpNewPendingUsers)) {
      enqueueSnackbar('User permissions saved successfully', { variant: 'success' });
      handleManageCompanyUsersDialogClose();
    }
  }, [
    formatEmailsForSaving,
    firmId,
    updatePermissionsForCompOrFundRole,
    handleManageCompanyUsersDialogClose,
    enqueueSnackbar,
  ]);

  return (
    <>
      {!isEmpty(newPendingUsers) && (
        <UserDetailsDialog
          open={openUserDetailsDialog}
          onClose={() => setOpenUserDetailsDialog(false)}
          newPendingUsers={newPendingUsers}
          firmId={firmId}
          companyList={companyList}
          fundList={fundList}
          handleManageCompanyUsersDialogClose={handleManageCompanyUsersDialogClose}
        />
      )}
      <div style={{ overflow: 'hidden' }}>
        <TextField
          label="Search Companies"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <CompanyUsersTable
          filteredCompanies={filteredCompanies}
          firmUsers={firmUsers}
          emails={emails}
          setEmails={setEmails}
          firmId={firmId}
        />
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="contained" color="primary" onClick={handleSavePermissions} disabled={isEmpty(emails)}>
            Add Users
          </Button>
        </Box>
      </div>
    </>
  );
};

export default ManageCompanyUsersLedger;
